import { auditLogPlugin } from './modules/audit-log';
import { coreExtensionData } from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  EnvironmentAccessGate,
  auditAccess,
  collaboratorAccess,
  environmentAccess,
} from './modules/environments/EnvironmentAccessGate';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import { UserSettingsGeneral } from '@backstage/plugin-user-settings';
import { Box } from '@material-ui/core';
import { ProfileGithubCard } from './modules/users/ProfileGithubCard';
import { createApp } from '@backstage/frontend-defaults';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { ScaffolderClient } from '@backstage/plugin-scaffolder';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { DefaultEntityPresentationApi } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  defaultEntityPresentation,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import orgPlugin from '@backstage/plugin-org/alpha';
import rbacPlugin from '@backstage-community/plugin-rbac';
import { navModule } from './modules/nav';
import { homeModule } from './modules/home';
import { usersPlugin } from './modules/users';
import { serversPlugin, databasesPlugin } from './modules/infrastructure';
import { provisioningModule } from './modules/provisioning';
import { environmentsModule } from './modules/environments';
import { authModule } from './modules/auth';
import { catalogFiltersModule } from './modules/catalog';
import { deleteCatalogModule } from './modules/catalog/useDeleteCatalogMenuProps';

export default createApp({
  features: [
    auditLogPlugin,
    scaffolderPlugin.withOverrides({
      extensions: [
        scaffolderPlugin.getExtension('api:scaffolder').override({
          params: defineParams =>
            defineParams({
              api: scaffolderApiRef,
              deps: {
                discoveryApi: discoveryApiRef,
                scmIntegrationsApi: scmIntegrationsApiRef,
                fetchApi: fetchApiRef,
                identityApi: identityApiRef,
              },
              // Finite log responses avoid SSE buffering by deployment proxies.
              factory: deps =>
                new ScaffolderClient({ ...deps, useLongPollingLogs: true }),
            }),
        }),
      ],
    }),
    authModule,
    catalogFiltersModule,
    deleteCatalogModule,
    catalogPlugin.withOverrides({
      extensions: [
        catalogPlugin.getExtension('page:catalog/entity').override({
          factory(originalFactory, { inputs }) {
            const contents = inputs.contents.map(content => {
              const path = content.get(coreExtensionData.routePath);
              if (
                ![
                  '/environments',
                  '/tools',
                  '/collaborators',
                  '/audit-log',
                ].includes(path || '')
              )
                return content;
              return {
                ...content,
                get(ref: any) {
                  if (
                    ref.id === EntityContentBlueprint.dataRefs.filterFunction.id
                  ) {
                    const original = content.get(
                      EntityContentBlueprint.dataRefs.filterFunction,
                    );
                    return (entity: any) =>
                      (path === '/collaborators'
                        ? collaboratorAccess
                        : path === '/audit-log'
                          ? auditAccess
                          : environmentAccess
                      ).get(stringifyEntityRef(entity)) === true &&
                      (!original || original(entity));
                  }
                  return content.get(ref);
                },
              };
            });
            const result = originalFactory({ inputs: { ...inputs, contents } });
            return [...result].map(value =>
              value.id === coreExtensionData.reactElement.id
                ? coreExtensionData.reactElement(
                    <EnvironmentAccessGate>
                      {result.get(coreExtensionData.reactElement)}
                    </EnvironmentAccessGate>,
                  )
                : value,
            );
          },
        }),
        catalogPlugin.getExtension('api:catalog/entity-presentation').override({
          params: defineParams =>
            defineParams({
              api: entityPresentationApiRef,
              deps: { catalogApi: catalogApiRef },
              factory: ({ catalogApi }) =>
                DefaultEntityPresentationApi.create({
                  catalogApi,
                  renderer: {
                    async: true,
                    render: ({ entity, entityRef, context }) => ({
                      snapshot: {
                        ...defaultEntityPresentation(
                          entity || entityRef,
                          context,
                        ),
                        primaryTitle:
                          entity?.metadata.title ||
                          entity?.metadata.name ||
                          parseEntityRef(entityRef).name,
                      },
                    }),
                  },
                }),
            }),
        }),
      ],
    }),
    orgPlugin,
    usersPlugin,
    serversPlugin,
    databasesPlugin,
    userSettingsPlugin.withOverrides({
      extensions: [
        userSettingsPlugin
          .getExtension('sub-page:user-settings/general')
          .override({
            params: {
              path: 'general',
              title: 'General',
              loader: async () => (
                <>
                  <UserSettingsGeneral />
                  <Box mt={3}>
                    <ProfileGithubCard />
                  </Box>
                </>
              ),
            },
          }),
      ],
    }),
    rbacPlugin,
    navModule,
    homeModule,
    provisioningModule,
    environmentsModule,
  ],
});
